import { makeAutoObservable, runInAction } from 'mobx'
import { handleError } from 'API'
import { acceptInvitation, declineInvitation } from 'API/invitation'
import { getMyTeams } from 'API/team'
import { TeamListItem } from 'types/auth'
import { RootStore } from '.'

const FILE = 'src/stores/teamsStore.ts'

/**
 * The signed-in user's team memberships, for the play-profile Teams preview.
 *
 * Reduced to that. The store used to drive the Teams page as well — create,
 * archive, accept/reject invite, leave, detail fetch, a shared detail edit-mode
 * flag for the Topbar — all from the era when a Team *was* an Organization. The
 * Teams page now manages the organization's teams and calls `API/team` directly,
 * which left every one of those members with no consumer.
 */
class TeamsStore {
  rootStore: RootStore

  teams: TeamListItem[] = []
  isLoading = false

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)
    this.rootStore = rootStore
  }

  /**
   * Invite actions for the parked mobile Teams view (`TeamsPage/legacy-mobile`).
   *
   * That view is not mounted anywhere today — it is kept intact as the starting
   * point for the mobile pass on the new Teams page, and these two methods with it,
   * so the component still compiles and still behaves as it did.
   */
  public async acceptInvite(token: string): Promise<void> {
    try {
      await acceptInvitation(token)
      // Accepting grants a new org membership, so the memberships refresh too:
      // the sidebar gates team-scoped sections off authStore, and without this the
      // new team's sections stay hidden until a full reload.
      await Promise.all([this.fetchTeams(), this.rootStore.authStore.fetchMyMemberships()])
    } catch (error) {
      handleError(error as Error, FILE, 'acceptInvite', false, true)
    }
  }

  public async rejectInvite(token: string): Promise<void> {
    try {
      await declineInvitation(token)
      await this.fetchTeams()
    } catch (error) {
      handleError(error as Error, FILE, 'rejectInvite', false, true)
    }
  }

  public async fetchTeams(): Promise<void> {
    runInAction(() => {
      this.isLoading = true
    })
    try {
      const teams = await getMyTeams()
      runInAction(() => {
        this.teams = teams ?? []
      })
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }
}

export default TeamsStore
